import { z } from 'zod';

export const InscricaoPushSchema = z.object({
  endpoint: z.string().url().max(500),
  chaves: z.object({ p256dh: z.string().min(10), auth: z.string().min(10) }),
});
export type InscricaoPush = z.infer<typeof InscricaoPushSchema>;

/**
 * Os assuntos que o painel avisa, e o vocabulário único dos três lados.
 *
 * Antes eram dois: uma lista aqui que ninguém importava e um union escrito à
 * mão dentro do serviço da API. Duas listas para a mesma coisa é uma que
 * envelhece calada — quem acrescentasse um aviso num lado nunca saberia do
 * outro.
 */
export const ASSUNTOS_AVISO = [
  'permissao',
  'sessao_concluida',
  'atencao',
  'transcricao',
  'maquina',
  'login',
  'backup',
  'disco',
  'cerebro',
  'teste',
] as const;
export const AssuntoAvisoSchema = z.enum(ASSUNTOS_AVISO);
export type AssuntoAviso = (typeof ASSUNTOS_AVISO)[number];

/** Rótulos em pt-BR, para a tela não repetir a chave técnica. */
export const ROTULO_ASSUNTO: Record<AssuntoAviso, string> = {
  permissao: 'Pergunta ou permissão esperando',
  sessao_concluida: 'Sessão terminou o trabalho',
  atencao: 'Sessão precisa de atenção',
  transcricao: 'Transcrição de gravação pronta',
  maquina: 'Máquina saiu ou voltou do ar',
  login: 'Login do Claude expirando',
  backup: 'Backup atrasado',
  disco: 'Disco enchendo',
  cerebro: 'Cérebro fora do ar ou ingestão atrasada',
  teste: 'Teste de aviso',
};

/**
 * O padrão de quem nunca escolheu nada.
 *
 * `transcricao` nasce desligado: o ditado do compositor usa a mesma esteira, e
 * avisar a cada frase ditada é o tipo de ruído que ensina a pessoa a ignorar a
 * notificação — aí o aviso que importava passa batido junto.
 */
export const PREFERENCIAS_AVISO_PADRAO: Record<AssuntoAviso, boolean> = {
  permissao: true,
  sessao_concluida: true,
  atencao: true,
  transcricao: false,
  maquina: true,
  login: true,
  backup: true,
  disco: true,
  cerebro: true,
  teste: true,
};

/**
 * `partialRecord`, não `record`: guardar só o que a pessoa mexeu é o que faz um
 * assunto novo nascer com o padrão certo para quem já tinha preferências
 * salvas. Um record completo congelaria a escolha do dia em que foi gravado.
 */
export const PreferenciasAvisoSchema = z.object({
  assuntos: z.partialRecord(AssuntoAvisoSchema, z.boolean()).default({}),
});
export type PreferenciasAviso = z.infer<typeof PreferenciasAvisoSchema>;

/**
 * Diz se este assunto pode virar aviso para quem tem estas preferências.
 *
 * Função pura de propósito: é ela que decide se um push sai, e uma regra que
 * decide isso não pode morar dentro de um service com banco na frente. O
 * `teste` sempre passa — é o botão que a pessoa aperta justamente para
 * descobrir se os avisos chegam neste aparelho, e um teste silenciado
 * responderia "nada chegou" para uma configuração perfeita.
 */
export function querAviso(preferencias: unknown, assunto: AssuntoAviso): boolean {
  if (assunto === 'teste') return true;
  const lido = PreferenciasAvisoSchema.safeParse(preferencias ?? {});
  const escolhidos = lido.success ? lido.data.assuntos : {};
  return escolhidos[assunto] ?? PREFERENCIAS_AVISO_PADRAO[assunto];
}

/**
 * Turno curto não avisa.
 *
 * Uma pergunta de dez segundos termina antes de a pessoa largar o aparelho, e
 * avisar sobre ela é interromper quem já está olhando.
 */
export const TURNO_MINIMO_AVISAVEL_MS = 30_000;

/**
 * Gravação curta não avisa, mesmo com o assunto ligado: é ditado, e quem ditou
 * está com o compositor na frente esperando o texto aparecer.
 */
export const TRANSCRICAO_MINIMA_AVISAVEL_MS = 60_000;
